import { SESSIONS_TEST_IDS } from '@/components/admin/sessions/contracts';
import { useFold } from '@/components/admin/sessions/walk/walk-fold';
import {
  NPC_AGAIN_LEAD,
  NPC_AGAIN_TAIL,
  REPLAY_AGAIN_LEAD,
  REPLAY_MODE_LABEL,
  REPLAY_MODE_SENTENCE,
  REPLAY_SOURCE_KIND_LABEL,
  npcSourcesLine,
  replayCostsSentence,
  replayIssueOf,
  replayLaunchLine,
  replayNpcLabel,
  replayOriginFact,
  replayPanelTitle,
  replayPassLine,
  replaySeriesOf,
  replaySourceSize,
  replayStartedLabel,
} from '@/lib/replay-mark';
import { costLabel } from '@/lib/session-labels';
import type { SessionReplayFull, SessionStatus } from '@matter_extractor/shared';
import { Icon } from '@pms-digital/design-system';
import { Link } from '@tanstack/react-router';
import { Fragment, type ReactNode, useId, useState } from 'react';

/**
 * The panel « Rejeu par Claude » of the parcours (rejeu.md §12, variante A of
 * the review of the maquettes, issue #193): between the head and the figures,
 * on a session Claude played in the place of a person - and mounted on no other.
 *
 * ## Why a panel and not a ninth tile
 *
 * « La nature de la session change la lecture de toute la page, et les tuiles
 * restent des chiffres » (maquettes/rejeu, README): that a session was played by
 * Claude is the first thing to know before reading its parcours, so it stands
 * above the figures, framed in the violet of the pill, and the eight tiles below
 * stay what they were.
 *
 * ## What it holds, in the order of the maquette
 *
 * The line « lancé par ... » (the operator, the instant, the declared model),
 * three facts - the Mode with its sentence, the Source with its kind and size,
 * and the replay this one replayed - then the two texts the session kept, each
 * UNFOLDED in place in a scrolling box rather than behind a page of their own,
 * and the sentence that says how to play the same case again, with the
 * identifier to copy.
 *
 * ## A replay of an NPC, and who played it (npc.md §13.2, issue #210)
 *
 * The panel gains what an NPC and a series add, and keeps the rest: the NPC and its version (a link
 * to its fiche), the series and the pass, « Les sources du NPC » frozen at their version, the issue,
 * and the Modèles du dispositif the replay played. The title says who played - « Rejeu joué par le
 * serveur », or « Rejeu par Claude » as ever - and a replay the server played names the real model
 * and tells the player's cost apart from the device's. « Regarder » (lot E) opens
 * the replay full window, live or recorded.
 *
 * ## Folded on one line, « Détails ▾ » unfolds it (issue #225)
 *
 * The panel arrives FOLDED, as the maquette draws it (`Main.dc.html`, « Rejeu »): the title, then
 * one sentence - the NPC and its version (a link to its fiche), the pass, who played, the issue, the
 * player's cost - and on the right « Détails ▾ » / « Masquer ▴ » (`aria-expanded`). Unfolded, it
 * says what it said before the fold: the line « lancé par ... » beside the title, the facts, the
 * two texts, the sentence to play it again. A replay with no NPC (#193) says the line with what it
 * has - its mode and the label of its source. The state is the component's own (`useFold`, the fold
 * of the blocks of analysis): a posture of the reading, never the URL's. The body is UNMOUNTED when
 * folded, not hidden: what the e2e suite reads is what an unfolded panel draws.
 *
 * ## The identifier is the whole one
 *
 * `fromReplay` takes the identifier of a replay, which is its session's: the
 * eight first characters would be a name an operator can say aloud and a tool
 * could not resolve. It is drawn in fixed pitch and `select-all`, so a refused
 * clipboard (a plain-HTTP dev stack) still leaves it one click from Ctrl+C.
 */

export interface ReplayPanelProps {
  /** The identifier of the session, which is the identifier of the replay. */
  readonly sessionId: string;
  readonly replay: SessionReplayFull;
  /** The status of the session: the issue of a replay of an NPC reads it. */
  readonly status?: SessionStatus | undefined;
  /** The cost of the device (the column Coût of the list), which the player's is told apart from. */
  readonly deviceCostEur?: number | undefined;
  /** The number of the Modèles the session froze, or null when it froze none. */
  readonly modelsVersion?: number | null | undefined;
}

/** What « Copier » says after a press: the identifier is in the clipboard, or it could not be put there. */
type CopyState = 'idle' | 'copied' | 'failed';

export function ReplayPanel({
  sessionId,
  replay,
  status,
  deviceCostEur,
  modelsVersion = null,
}: ReplayPanelProps) {
  const titleId = useId();
  const sourceBoxId = useId();
  const instructionBoxId = useId();
  const fold = useFold(false);
  const [sourceOpen, setSourceOpen] = useState(false);
  const [instructionOpen, setInstructionOpen] = useState(false);
  const [copy, setCopy] = useState<CopyState>('idle');
  const origin = replayOriginFact(replay.fromReplay);
  const npc = replay.npc ?? null;
  const series = replaySeriesOf(replay.series);
  const launch = replayLaunchLine(replay);
  const issue = status === undefined ? null : replayIssueOf(status);
  const served = replay.player === 'server';

  const summary = summaryParts({ replay, launch, npc, issue, served });

  async function copyId(): Promise<void> {
    try {
      await navigator.clipboard.writeText(sessionId);
      setCopy('copied');
    } catch {
      // No clipboard, or refused: the identifier stays selectable beside the button.
      setCopy('failed');
    }
    setTimeout(() => setCopy('idle'), 2000);
  }

  return (
    <section
      data-testid={SESSIONS_TEST_IDS.replayPanel}
      data-mode={replay.mode}
      data-source-kind={replay.sourceKind}
      aria-labelledby={titleId}
      className="flex flex-col gap-3 rounded-md border-2 border-violet-deep/25 bg-violet-tint/40 px-4.5 py-4"
    >
      <div className="flex flex-wrap items-center gap-x-4 gap-y-1">
        <h2 id={titleId} className="font-bold text-body-lg text-violet-deep leading-6">
          {replayPanelTitle(replay.player)}
        </h2>
        {fold.open ? (
          <p data-testid={SESSIONS_TEST_IDS.replayStarted} className="text-gray-600 text-small">
            lancé par <b className="font-semibold text-gray-800">{replay.operator}</b> le{' '}
            {replayStartedLabel(replay.startedAt)}, {launch.from}
            {launch.model === null ? null : (
              <>
                {' · '}
                {launch.playedBy ? 'joué par ' : null}
                {launch.playedBy ? (
                  <b className="font-semibold text-gray-800">{launch.model}</b>
                ) : (
                  launch.model
                )}
                {launch.declared ? ' (déclaré)' : null}
              </>
            )}
          </p>
        ) : (
          <p
            data-testid={SESSIONS_TEST_IDS.replaySummary}
            className="min-w-0 flex-1 basis-80 text-gray-700 text-small"
          >
            {summary.map((part, index) => (
              <Fragment key={part.key}>
                {index === 0 ? null : ' · '}
                {part.node}
              </Fragment>
            ))}
          </p>
        )}
        <button
          type="button"
          data-testid={SESSIONS_TEST_IDS.replayToggle}
          aria-expanded={fold.open}
          aria-controls={fold.open ? fold.bodyId : undefined}
          onClick={fold.toggle}
          className="ml-auto inline-flex h-8 cursor-pointer items-center rounded-md px-2.5 font-semibold text-small text-violet-deep hover:bg-violet-tint focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-bleu-nuit"
        >
          {fold.open ? 'Masquer ▴' : 'Détails ▾'}
        </button>
      </div>

      {fold.open ? (
        <div id={fold.bodyId} className="flex flex-col gap-3">
          <div
            data-testid={SESSIONS_TEST_IDS.replayFacts}
            className={`grid gap-3 ${npc === null ? 'sm:grid-cols-3' : 'sm:grid-cols-2 lg:grid-cols-3'}`}
          >
            <Fact testId={SESSIONS_TEST_IDS.replayMode} label="Mode">
              <b className="font-semibold text-body text-gray-900">
                {REPLAY_MODE_LABEL[replay.mode]}
              </b>
              <span className="text-gray-600 text-caption">
                {REPLAY_MODE_SENTENCE[replay.mode]}
              </span>
            </Fact>
            {npc === null ? null : (
              <Fact testId={SESSIONS_TEST_IDS.replayNpc} label="NPC">
                <Link
                  to="/admin/npc/$npcId"
                  params={{ npcId: npc.id }}
                  data-testid={SESSIONS_TEST_IDS.replayNpcLink}
                  className="break-words font-semibold text-body text-bleu-nuit hover:underline"
                >
                  {replayNpcLabel(npc)}
                </Link>
              </Fact>
            )}
            {series === null ? null : (
              <Fact testId={SESSIONS_TEST_IDS.replaySeries} label="Série">
                <b className="break-words font-semibold text-body text-gray-900">{series.text}</b>
                {replayPassLine(replay.pass) === null ? null : (
                  <span className="text-gray-600 text-caption">{replayPassLine(replay.pass)}</span>
                )}
              </Fact>
            )}
            {npc === null ? (
              <Fact testId={SESSIONS_TEST_IDS.replaySource} label="Source">
                <b className="break-words font-semibold text-body text-gray-900">
                  « {replay.sourceLabel} »
                </b>
                <span className="text-gray-600 text-caption">
                  {REPLAY_SOURCE_KIND_LABEL[replay.sourceKind]} ·{' '}
                  {replaySourceSize(replay.sourceChars)}
                </span>
              </Fact>
            ) : (
              <Fact testId={SESSIONS_TEST_IDS.replaySource} label="Source">
                <b className="break-words font-semibold text-body text-gray-900">
                  Les sources du NPC
                </b>
                <span className="text-gray-600 text-caption">
                  {npcSourcesLine(npc.versionNumber, replay.sourceChars)}
                </span>
              </Fact>
            )}
            {served ? (
              <Fact testId={SESSIONS_TEST_IDS.replayPlayer} label="Joueur">
                <b className="break-words font-semibold text-body text-gray-900">
                  {launch.model ?? 'modèle inconnu'}
                </b>
                {deviceCostEur === undefined ? null : (
                  <span className="text-gray-600 text-caption">
                    {replayCostsSentence(replay.playerCostEur ?? 0, deviceCostEur)}
                  </span>
                )}
              </Fact>
            ) : null}
            {npc !== null && issue !== null ? (
              <Fact testId={SESSIONS_TEST_IDS.replayIssue} label="Issue">
                <b className="font-semibold text-body text-gray-900">{issue.word}</b>
                <span className="text-gray-600 text-caption">{issue.detail}</span>
              </Fact>
            ) : null}
            {npc !== null && modelsVersion !== null ? (
              <Fact testId={SESSIONS_TEST_IDS.replayModels} label="Modèles du dispositif">
                <b className="font-semibold text-body text-gray-900">v{modelsVersion}</b>
                <span className="text-gray-600 text-caption">figés au lancement</span>
              </Fact>
            ) : null}
            {/* A replay of an NPC with nothing it replayed reads no « Rejoue: aucun »: the line is noise there. */}
            {npc !== null && origin.kind === 'none' ? null : (
              <Fact testId={SESSIONS_TEST_IDS.replayOrigin} label="Rejoue" kind={origin.kind}>
                {origin.kind === 'linked' && origin.replayId !== null ? (
                  <Link
                    to="/admin/sessions/$sessionId"
                    params={{ sessionId: origin.replayId }}
                    data-testid={SESSIONS_TEST_IDS.replayOriginLink}
                    className="font-semibold text-body text-bleu-nuit hover:underline"
                  >
                    {origin.label}
                  </Link>
                ) : (
                  <b
                    className={`font-semibold text-body ${origin.kind === 'none' ? 'text-gray-500' : 'text-gray-900'}`}
                  >
                    {origin.label}
                  </b>
                )}
                {origin.detail === null ? null : (
                  <span className="text-gray-600 text-caption">{origin.detail}</span>
                )}
              </Fact>
            )}
          </div>

          <div className="flex flex-wrap gap-2">
            <Unfold
              testId={SESSIONS_TEST_IDS.replaySourceToggle}
              open={sourceOpen}
              controls={sourceBoxId}
              openLabel="Masquer la source"
              closedLabel="Voir la source"
              onToggle={() => setSourceOpen((open) => !open)}
            />
            <Unfold
              testId={SESSIONS_TEST_IDS.replayInstructionToggle}
              open={instructionOpen}
              controls={instructionBoxId}
              openLabel="Masquer la consigne servie"
              closedLabel="Voir la consigne servie"
              onToggle={() => setInstructionOpen((open) => !open)}
            />
            <Link
              to="/admin/npc/regarder/$id"
              params={{ id: sessionId }}
              data-testid="sessions-rejeu-regarder"
              className="inline-flex h-8 items-center rounded-md border-2 border-gray-200 bg-white px-3 font-semibold text-gray-700 text-small no-underline hover:border-gray-300"
            >
              Regarder
            </Link>
          </div>

          {sourceOpen ? (
            <TextBox
              id={sourceBoxId}
              testId={SESSIONS_TEST_IDS.replaySourceText}
              label={`Source du rejeu : ${replay.sourceLabel}`}
              text={replay.sourceText}
            />
          ) : null}
          {instructionOpen ? (
            <div className="flex flex-col gap-1">
              <p className="text-gray-600 text-caption">
                Consigne telle que servie à Claude · empreinte{' '}
                <span className="font-mono text-gray-800">{replay.instructionFingerprint}</span>
              </p>
              <TextBox
                id={instructionBoxId}
                testId={SESSIONS_TEST_IDS.replayInstructionText}
                label="Consigne servie à Claude"
                text={replay.instruction}
              />
            </div>
          ) : null}

          <p
            data-testid={SESSIONS_TEST_IDS.replayAgain}
            className="flex flex-wrap items-baseline gap-x-1.5 text-gray-600 text-caption"
          >
            {npc === null ? (
              <span>{REPLAY_AGAIN_LEAD}</span>
            ) : (
              <span>
                {NPC_AGAIN_LEAD}{' '}
                <Link
                  to="/admin/npc/$npcId"
                  params={{ npcId: npc.id }}
                  data-testid={SESSIONS_TEST_IDS.replayAgainNpcLink}
                  className="font-semibold text-bleu-nuit underline underline-offset-3 hover:text-bleu-nuit-deep"
                >
                  la fiche du NPC
                </Link>
                {series === null ? null : ' ou sa série'} ; {NPC_AGAIN_TAIL}
              </span>
            )}
            <span
              data-testid={SESSIONS_TEST_IDS.replayId}
              className="select-all break-all font-mono text-gray-800"
            >
              {sessionId}
            </span>
            <button
              type="button"
              data-testid={SESSIONS_TEST_IDS.replayCopy}
              data-state={copy}
              onClick={() => void copyId()}
              className="cursor-pointer font-semibold text-bleu-nuit underline underline-offset-3 hover:text-bleu-nuit-deep focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-bleu-nuit"
            >
              {copy === 'copied' ? 'Copié' : copy === 'failed' ? 'Copie impossible' : 'Copier'}
            </button>
            <span aria-live="polite" className="sr-only">
              {copy === 'copied' ? 'Identifiant copié.' : null}
              {copy === 'failed'
                ? 'Le presse-papiers est indisponible : l’identifiant est à sélectionner.'
                : null}
            </span>
          </p>
        </div>
      ) : null}
    </section>
  );
}

interface SummaryPart {
  readonly key: string;
  readonly node: ReactNode;
}

/**
 * What the folded line says, in the order of the maquette: the NPC and its version in a link, the
 * pass, who played, the issue, the player's cost. A replay with no NPC says its mode and the label
 * of its source instead (what it has), and a part the replay does not carry is left out rather
 * than written « inconnu ».
 */
function summaryParts({
  replay,
  launch,
  npc,
  issue,
  served,
}: {
  replay: SessionReplayFull;
  launch: ReturnType<typeof replayLaunchLine>;
  npc: SessionReplayFull['npc'];
  issue: ReturnType<typeof replayIssueOf> | null;
  served: boolean;
}): readonly SummaryPart[] {
  const parts: SummaryPart[] = [];
  const pass = replayPassLine(replay.pass);
  if (npc === null || npc === undefined) {
    parts.push({
      key: 'mode',
      node: (
        <>
          <b className="font-semibold text-gray-900">{REPLAY_MODE_LABEL[replay.mode]}</b>
          {` · « ${replay.sourceLabel} »`}
        </>
      ),
    });
  } else {
    parts.push({
      key: 'npc',
      node: (
        <>
          NPC{' '}
          <Link
            to="/admin/npc/$npcId"
            params={{ npcId: npc.id }}
            data-testid={SESSIONS_TEST_IDS.replaySummaryNpcLink}
            className="break-words font-semibold text-bleu-nuit hover:underline"
          >
            {replayNpcLabel(npc)}
          </Link>
        </>
      ),
    });
  }
  if (pass !== null) parts.push({ key: 'pass', node: pass });
  if (launch.model !== null) {
    parts.push({
      key: 'player',
      node: launch.playedBy ? (
        <>
          joué par <b className="font-semibold text-gray-900">{launch.model}</b>
        </>
      ) : (
        `${launch.model} (déclaré)`
      ),
    });
  }
  if (npc !== null && npc !== undefined && issue !== null) {
    parts.push({
      key: 'issue',
      node: (
        <>
          issue <b className="font-semibold text-gray-900">{issue.word}</b>
        </>
      ),
    });
  }
  if (served && replay.playerCostEur !== null && replay.playerCostEur !== undefined) {
    parts.push({ key: 'cost', node: `joueur ${costLabel(replay.playerCostEur)}` });
  }
  return parts;
}

/** One of the three facts: a small caps label, a value, and what qualifies it. */
function Fact({
  testId,
  label,
  kind,
  children,
}: {
  testId: string;
  label: string;
  /** The shape of the fact « Rejoue », for a suite to read. */
  kind?: string;
  children: ReactNode;
}) {
  return (
    <div data-testid={testId} data-kind={kind} className="flex min-w-0 flex-col gap-0.5">
      <span className="font-semibold text-gray-600 text-caption uppercase tracking-wide">
        {label}
      </span>
      {children}
    </div>
  );
}

/** « Voir la source » / « Masquer la source »: a disclosure, so `aria-expanded` and the id of what it opens. */
function Unfold({
  testId,
  open,
  controls,
  openLabel,
  closedLabel,
  onToggle,
}: {
  testId: string;
  open: boolean;
  controls: string;
  openLabel: string;
  closedLabel: string;
  onToggle: () => void;
}) {
  return (
    <button
      type="button"
      data-testid={testId}
      aria-expanded={open}
      aria-controls={open ? controls : undefined}
      onClick={onToggle}
      className={`inline-flex h-8 cursor-pointer items-center gap-1.5 rounded-md border-2 bg-white px-3 font-semibold text-small focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-bleu-nuit ${
        open
          ? 'border-violet-deep text-violet-deep'
          : 'border-gray-200 text-gray-700 hover:border-gray-300'
      }`}
    >
      {open ? openLabel : closedLabel}
      <Icon name={open ? 'expand_less' : 'expand_more'} size={14} />
    </button>
  );
}

/**
 * A text unfolded in place, in a box that scrolls.
 *
 * Focusable and named, so the keyboard reaches it: a scrolling region nobody can
 * tab to is a text only a mouse can finish reading. `whitespace-pre-wrap`,
 * because a source is kept as Claude received it - its paragraphs are the
 * person's.
 */
function TextBox({
  id,
  testId,
  label,
  text,
}: {
  id: string;
  testId: string;
  label: string;
  text: string;
}) {
  return (
    <section
      id={id}
      data-testid={testId}
      aria-label={label}
      // biome-ignore lint/a11y/noNoninteractiveTabindex: the rule guards against tab stops that do nothing; this one scrolls.
      tabIndex={0}
      className="max-h-40 overflow-auto whitespace-pre-wrap break-words rounded-md border border-gray-200 bg-white px-3 py-2.5 text-gray-800 text-small leading-5 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-bleu-nuit"
    >
      {text}
    </section>
  );
}
